/**
 * Conservative terminal markdown rendering for streamed assistant text. It is LINE-BUFFERED: deltas are
 * accumulated until a newline, then each COMPLETE line is rendered — so an inline span (`**bold**`,
 * `` `code` ``, `[link](url)`) can never be mangled by a chunk boundary that splits it. A safe subset is
 * transformed: headings, **bold**, `code`, `[links](url)`, fenced code blocks, `---` rules, and GFM tables.
 *
 * Tables need every row before columns can be aligned, which the per-line commit model can't do mid-stream.
 * The CHEAP strategy: BUFFER a contiguous table block (rows held, not committed raw) and emit it aligned once
 * the block ends (first non-table line, or `flush()`). Rows therefore "snap" into alignment at block end
 * rather than rendering live. A future full-streaming upgrade only changes WHEN the block is flushed (e.g.
 * re-render the partial block on every feed) — the parsing/alignment in `renderTableLines` stays as-is.
 *
 * Both renderers share `renderTableLines`: the STREAM (`MarkdownStream`, live turns) and the BLOCK renderer
 * (`renderMdBlock`, resume/replay of whole transcripts). They drifted once — replay printed raw `|` pipes
 * where the live turn showed columns — so alignment must stay in the one function, not per-caller.
 *
 * The caller gates all of this to a color-capable TTY — piped / NO_COLOR output never reaches here, stays raw.
 */

import { needsBidi, bidiLine, appBidi } from './bidi';

export type Paint = (s: string) => string;

/** Reorder a logical text segment into visual (display) order for the TTY — same path the line editor uses on
 *  input (cli/bidi.ts). Applied to PLAIN text BEFORE ANSI styling: within a same-direction run character order
 *  is preserved, so markdown markers (`**`, `` ` ``, `[]()`) stay adjacent to their content and the later
 *  styling pass still matches. Pure-LTR text is returned untouched (fast path). */
const vis = (s: string): string => (appBidi() && needsBidi(s) ? bidiLine(s).visual : s);
export interface MdPalette {
  bold: Paint;
  dim: Paint;
  cyan: Paint;
  italic?: Paint;  // *x* / _x_ — falls back to no styling (text kept, markers dropped)
  strike?: Paint;  // ~~x~~   — falls back to no styling
  green?: Paint;   // string literals in highlighted code fences — falls back to cyan
  /** Render `text` as a hyperlink to `url` (e.g. OSC 8). Optional — falls back to cyan link text. */
  link?: (text: string, url: string) => string;
}

// ── Fenced-code syntax highlighting (lean, regex-token based — not a real parser) ──
const C_KW = new Set('const let var function return if else for while do switch case break continue class new async await try catch finally throw import export from default type interface extends implements public private protected readonly static void null undefined true false this super yield typeof instanceof in of enum namespace struct fn pub mut match impl use go defer chan func package var range nil'.split(' '));
const PY_KW = new Set('def return if elif else for while class import from as with try except finally raise lambda pass break continue global nonlocal yield async await None True False not and or in is del assert print self'.split(' '));
const SH_KW = new Set('if then else elif fi for while do done case esac function local export return echo exit set source in'.split(' '));
const HASH_COMMENT = /^(sh|bash|zsh|shell|py|python|rb|ruby|yaml|yml|toml|ini|make|makefile|dockerfile|r)$/;
/** Keyword set for a fence language tag; null → no highlighting (unknown/absent lang keeps legacy all-cyan). */
function kwFor(lang?: string): Set<string> | null {
  if (!lang) return null;
  const l = lang.toLowerCase();
  if (/^(js|jsx|ts|tsx|javascript|typescript|java|c|h|cpp|cc|cs|go|rust|rs|swift|kotlin|kt|php|scala|dart)$/.test(l)) return C_KW;
  if (/^(py|python)$/.test(l)) return PY_KW;
  if (/^(sh|bash|zsh|shell|console)$/.test(l)) return SH_KW;
  if (/^(json|yaml|yml|toml|sql|html|xml|css)$/.test(l)) return new Set(); // strings/numbers/comments only
  return null;
}
/** Color one code line: comments dim, strings green, numbers cyan, keywords bold — rest plain.
 *  Unknown language → the whole line in cyan (the pre-highlighting behavior). Best-effort by design:
 *  a `#`/`//` inside a string may be taken for a comment; acceptable for terminal display. */
export function highlightCode(line: string, lang: string | undefined, p: MdPalette): string {
  const kws = kwFor(lang);
  if (!kws) return p.cyan(line);
  const cm = (HASH_COMMENT.test(lang!.toLowerCase()) ? /(^|\s)#.*$/ : /\/\/.*$|\/\*.*$/).exec(line);
  let code = line, comment = '';
  if (cm) { const at = cm.index + (cm[1]?.length ?? 0); comment = line.slice(at); code = line.slice(0, at); }
  const str = p.green ?? p.cyan;
  const re = /("(?:[^"\\]|\\.)*"?|'(?:[^'\\]|\\.)*'?|`[^`]*`?)|\b(\d+(?:\.\d+)?)\b|\b([A-Za-z_]\w*)\b/g;
  const out = code.replace(re, (m, s, num, word) => {
    if (s) return str(s);
    if (num) return p.cyan(num);
    if (word && kws.has(word)) return p.bold(word);
    return m;
  });
  return out + (comment ? p.dim(comment) : '');
}

/** Strip INLINE markdown markers to recover the VISIBLE text — used for table column-width math (where the
 *  ANSI-styled form has a different byte length than what the eye sees) and for dim preview panels that
 *  show raw text without a full render. Inline-only: leading block markers (#, -) are NOT touched here so
 *  table-cell widths stay exact; the panel callsite handles those separately via `plainLine`. */
export function displayText(s: string): string {
  return s
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/(?<![\w*])[*_]([^*_\s][^*_]*?)[*_](?![\w*])/g, '$1');
}

/** Strip a whole line of markdown for a plain dim preview (no styling): inline markers + leading heading
 *  hashes / list markers. For panels like the duplex task-result preview — NOT for width-sensitive math. */
export function plainLine(s: string): string {
  return displayText(s).replace(/^\s{0,3}#{1,6}\s+/, '').replace(/^(\s*)[-*+]\s+/, '$1');
}

/** Render inline spans in one already-complete line. A single alternation pass so a `code` span is consumed
 *  as one unit — its inner `**` is never seen by the **bold** branch (a two-pass approach would re-bold it).
 *  Order: code → link → bold, so a `[**x**](u)` link text isn't pre-eaten by the bold branch. */
export function mdInline(line: string, p: MdPalette): string {
  const re = /(`[^`]+`)|(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(~~[^~]+~~)|((?<![\w*])[*_][^*_\s][^*_]*?[*_](?![\w*]))/g;
  return line.replace(re, (_m, code, link, bold, strike, italic) => {
    if (code) return p.cyan(code.slice(1, -1));
    if (link) {
      const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(link)!;
      return p.link ? p.link(m[1], m[2]) : p.cyan(m[1]);
    }
    if (bold) return p.bold(bold.slice(2, -2));
    if (strike) return (p.strike ?? ((s) => s))(strike.slice(2, -2));
    return (p.italic ?? ((s) => s))(italic.slice(1, -1)); // markers dropped even without a paint
  });
}

/** Render one COMPLETE non-table line to ANSI, threading fenced-code-block state across lines. `cols` widths
 *  the `---` horizontal rule (falls back to 80 when unknown). Both callers hold contiguous table rows back for
 *  `renderTableLines`, so a `|`-row only reaches here via that function's not-really-a-table fallback. */
export function renderMdLine(line: string, inFence: boolean, p: MdPalette, cols = 80, fenceLang?: string): { out: string; inFence: boolean; fenceLang?: string } {
  if (/^\s*```/.test(line)) { // fence marker: dim, toggle state, capture the language tag on open
    const lang = inFence ? undefined : /^\s*```\s*(\w+)/.exec(line)?.[1];
    return { out: p.dim(line), inFence: !inFence, fenceLang: lang };
  }
  if (inFence) return { out: highlightCode(line, fenceLang, p), inFence, fenceLang }; // code body: lang-aware highlight (cyan when unknown)
  if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) return { out: p.dim('─'.repeat(cols)), inFence }; // --- → full rule
  const h = line.match(/^(#{1,6})\s+(.*)$/);
  if (h) return { out: p.bold(vis(h[2])), inFence };                       // heading → bold, drop the leading #'s
  const q = line.match(/^(\s*)>\s?(.*)$/);
  if (q) return { out: q[1] + p.dim('▎ ') + p.dim(mdInline(vis(q[2]), p)), inFence }; // blockquote → dim bar + dim text
  const b = line.match(/^(\s*)[-*+]\s+(.*)$/);
  if (b) return { out: b[1] + p.cyan('•') + ' ' + mdInline(vis(b[2]), p), inFence }; // bullet → • (keeps indent)
  return { out: mdInline(vis(line), p), inFence };
}

/** Render a COMPLETE multi-line markdown block to ANSI (non-streaming) — for replaying history/transcripts
 *  where the text is already whole. Threads fence state across lines and wraps each rendered line to `cols`
 *  with a hanging indent. Contiguous table rows are collected and emitted aligned (same `renderTableLines`
 *  the live stream uses), so resumed history matches what was on screen live. Pure. */
export function renderMdBlock(text: string, p: MdPalette, cols = 80): string {
  let inFence = false, fenceLang: string | undefined;
  const out: string[] = [];
  let table: string[] = [];
  const flush = () => { if (table.length) { out.push(...renderTableLines(table, p, cols)); table = []; } };
  for (const line of text.split('\n')) {
    if (!inFence && isTableRow(line)) { table.push(line); continue; }
    flush();
    const r = renderMdLine(line, inFence, p, cols, fenceLang);
    inFence = r.inFence; fenceLang = r.fenceLang;
    out.push(r.inFence || /^\s*```/.test(line) ? r.out : wrapAnsi(r.out, cols, hangIndent(line)));
  }
  flush();
  return out.join('\n');
}

const T_PAD = '  ';  // left margin every table line carries
const T_GAP = 3;     // spaces between columns

/** Which openers an SGR closer cancels — `22` ends bold/dim, `39` ends any foreground colour, `0` ends all. */
const SGR_CLOSES: Record<string, RegExp> = {
  '0': /./, '22': /^[12]$/, '23': /^3$/, '24': /^4$/, '27': /^7$/, '29': /^9$/,
  '39': /^(3[0-7]|9[0-7])$/, '49': /^(4[0-7]|10[0-7])$/,
};

/** Wrap ONE styled cell to `w`, keeping every segment SGR-self-contained: reset what's still open at a segment's
 *  end and re-open it at the next segment's start. Without this a style split by the wrap (`**a long label**`)
 *  stays open at the column edge and bleeds its bold/colour across the gap into the next column, with the reset
 *  landing a line lower. Best-effort by design: compound tokens (`\x1b[1;36m`) count as openers and are cleared
 *  only by a full reset — our palettes emit one code per token. */
function wrapCell(styled: string, w: number): string[] {
  const segs = wrapAnsi(styled, w).split('\r\n');
  if (segs.length < 2 || !styled.includes('\x1b')) return segs; // unwrapped cell is already balanced
  const tok = new RegExp(ANSI_TOKEN.source, 'g');
  let open: string[] = [];
  return segs.map((seg) => {
    const prefix = open.join('');
    for (const m of seg.match(tok) ?? []) {
      const code = /^\x1b\[([0-9;]*)m$/.exec(m)?.[1];
      if (code === undefined) continue;                          // OSC-8 link wrapper: not an SGR, carries nothing
      const closes = SGR_CLOSES[code || '0'];                    // `\x1b[m` == `\x1b[0m`
      if (closes) open = open.filter((o) => !closes.test(/^\x1b\[([0-9;]*)m$/.exec(o)![1]));
      else open.push(m);
    }
    return prefix + seg + (open.length ? '\x1b[0m' : '');
  });
}

const T_MINCOL = 2; // a column budgeted below this can't wrap (wrapAnsi passes through at ≤1) — see `room`

/** Choose column widths that FIT `avail` total. Columns narrower than an equal share keep their natural width
 *  (short cells never get wrapped to make room for a hog); the surplus is shared by the wide ones. Water-fill:
 *  the largest cap where `sum(min(natural, cap))` still fits, then hand the remainder to the still-clipped
 *  columns left-to-right. Returns natural widths untouched when the table already fits.
 *
 *  `room` floors the budget at T_MINCOL per column: below that no grid can be drawn at all, and a 1-char
 *  budget is worse than useless (wrapAnsi returns the cell UNWRAPPED at ≤1, blowing the row out to its full
 *  natural width — the very overflow this function exists to prevent). Flooring instead overflows by a bounded
 *  few chars on an absurdly narrow terminal, which no real terminal hits. */
function fitWidths(natural: number[], avail: number): number[] {
  const total = natural.reduce((a, b) => a + b, 0);
  if (total <= avail || !natural.length) return natural;
  const room = Math.max(T_MINCOL * natural.length, avail);
  let cap = 1;
  for (let c = 1; c <= Math.max(...natural); c++) { // ≤ terminal width iterations — cheap, and exact
    if (natural.reduce((a, w) => a + Math.min(w, c), 0) <= room) cap = c; else break;
  }
  const w = natural.map((n) => Math.min(n, cap));
  let left = room - w.reduce((a, b) => a + b, 0);
  for (let i = 0; i < w.length && left > 0; i++) { // spread the rounding remainder over the clipped columns
    const take = Math.min(left, natural[i] - w[i]);
    w[i] += take; left -= take;
  }
  return w;
}

/** Align a contiguous block of GFM table rows into padded columns (header bolded + underlined), bounded to
 *  `cols` — cells too wide for their budgeted column WRAP onto extra lines within the column, so a wide table
 *  degrades into a taller one instead of overflowing (the terminal's own hard-wrap folds spill to column 0 and
 *  shreds the grid). Returns bare lines (no terminators) so both the streaming renderer (\r\n) and the block
 *  renderer (\n) can join them. A block with no `|---|` separator row isn't a real table — its rows render as
 *  normal lines instead. */
export function renderTableLines(rows: string[], p: MdPalette, cols = 80): string[] {
  const sepIdx = rows.findIndex((r, i) => i > 0 && r.includes('-') && /^\s*\|[\s:|-]+\|\s*$/.test(r));
  if (sepIdx < 0) return rows.map((r) => renderMdLine(r, false, p, cols).out);
  const cells = (r: string) => r.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  const data = rows.filter((_, i) => i !== sepIdx).map(cells); // header + body (drop the separator row)
  const ncol = Math.max(...data.map((r) => r.length));
  const natural = Array.from({ length: ncol }, (_, i) => Math.max(...data.map((r) => displayText(r[i] ?? '').length)));
  const widths = fitWidths(natural, cols - T_PAD.length - T_GAP * (ncol - 1));
  // Wrap every cell inside its column first: the row heights decide whether rows need blank-line separation.
  const grid = data.map((row, ri) => widths.map((w, ci) => {
    const c = row[ci] ?? '';
    const styled = ri === 0 ? p.bold(displayText(vis(c))) : mdInline(vis(c), p);
    return wrapCell(styled, w);
  }));
  // Once any row spans several lines, adjacent rows run together and the grid stops being readable — blank-line
  // them apart. A table whose rows are all one line stays compact (no gratuitous double-spacing).
  const airy = grid.slice(1).some((row) => row.some((c) => c.length > 1));
  const out: string[] = [];
  grid.forEach((col, ri) => {
    const header = ri === 0;
    if (airy && ri > 1) out.push('');
    const height = Math.max(...col.map((c) => c.length));
    for (let li = 0; li < height; li++) {
      out.push((T_PAD + widths.map((w, ci) => {
        const seg = col[ci][li] ?? '';
        return seg + ' '.repeat(Math.max(0, w - visWidth(seg))); // pad on VISIBLE width — ANSI is zero-width
      }).join(' '.repeat(T_GAP))).replace(/\s+$/, ''));
    }
    if (header) out.push(T_PAD + p.dim('─'.repeat(widths.reduce((a, b) => a + b, 0) + T_GAP * (ncol - 1))));
  });
  return out;
}

/** A `| a | b |` row — header, separator, or body. Prose rarely opens a line with `|`, so this is safe. */
function isTableRow(line: string): boolean {
  return /^\s*\|.*\|\s*$/.test(line);
}

// SGR color codes + OSC-8 hyperlink wrappers: present in styled output, zero display width.
const ANSI_TOKEN = /\x1b\[[0-9;]*m|\x1b\]8;;[^\x07\x1b]*(?:\x07|\x1b\\)/;
const WRAP_TOKENS = new RegExp(`${ANSI_TOKEN.source}|[ \\t]+|[^\\s\\x1b]+`, 'g');

/** Display width of a styled string: what the eye sees, with the zero-width escapes discounted. For grid math
 *  (column padding) where `.length` would count invisible bytes and skew every column after the first.
 *  NOTE: code units, so a wide glyph (emoji/CJK) undercounts as 1 — see tui-render-parity-remainders. */
function visWidth(s: string): number {
  return s.replace(new RegExp(ANSI_TOKEN.source, 'g'), '').length;
}

/** Wrap one styled (ANSI-bearing) line at WORD boundaries to `cols` visible chars; continuation rows get
 *  `indent` (hanging indent for bullets/quotes). Escapes are zero-width and never split; the terminal
 *  carries SGR attributes across the inserted newlines, so codes need no close/reopen. A single word wider
 *  than the row is hard-sliced (can't do better). Lines are joined with \r\n (raw-mode stream convention). */
export function wrapAnsi(s: string, cols: number, indent = ''): string {
  if (cols <= 1 || s.replace(new RegExp(ANSI_TOKEN.source, 'g'), '').length <= cols) return s;
  if (indent.length >= cols) indent = ''; // degenerate: indent eats the whole row → wrap flush (also guards the slice loop below)
  const tokens = s.match(WRAP_TOKENS) ?? [];
  const lines: string[] = [];
  let line = '', w = 0;
  const commit = () => { lines.push(line.replace(/[ \t]+$/, '')); line = indent; w = indent.length; };
  for (const t of tokens) {
    if (t.startsWith('\x1b')) { line += t; continue; }
    if (/^[ \t]+$/.test(t)) { // spaces never open a wrapped row — swallow them at a break
      if (w + t.length <= cols) { line += t; w += t.length; }
      else commit();
      continue;
    }
    let word = t;
    while (word && w + word.length > cols) { // `word &&`: a drained word must exit even if w is over (degenerate widths)
      if (w > indent.length) { commit(); continue; }      // push the word to the next row
      const take = Math.max(1, cols - w);                  // word alone exceeds the width → hard-slice
      line += word.slice(0, take); word = word.slice(take); commit();
    }
    line += word; w += word.length;
  }
  if (line !== indent || !lines.length) lines.push(line.replace(/[ \t]+$/, ''));
  return lines.join('\r\n');
}

/** Continuation-row indent for a wrapped SOURCE line: bullets and blockquotes hang under their text
 *  (marker width = 2), plain paragraphs wrap flush. */
export function hangIndent(line: string): string {
  const m = line.match(/^(\s*)(?:[-*+]|>)\s/);
  return m ? m[1] + '  ' : '';
}

/**
 * PROGRESSIVE streaming renderer. Text appears as it arrives (char-by-char): every `feed()` re-renders the
 * current in-progress line in place, so an inline `**bold**` / `` `code` `` shows literally while open and
 * "snaps" to its styled form the instant its closer arrives — and headings bold as you type. Complete lines
 * (ending in `\n`) are committed permanently. The in-progress line may wrap, so we clear ALL its physical rows
 * (not just the last) before re-drawing — no long-line corruption. `flush()` commits + resets (any in-progress
 * tail is already on screen; a buffered table block is emitted here). `cols` = terminal width for the wrap math.
 */
export class MarkdownStream {
  private buf = '';            // the current (uncommitted) logical line's text
  private inFence = false;
  private fenceLang?: string;  // language tag of the open fence (drives syntax highlighting)
  private lineRows = 0;        // physical rows the in-progress line occupied last draw (for the next clear)
  private tableBuf: string[] = []; // contiguous GFM table rows held until the block ends, then aligned
  constructor(private p: MdPalette, private cols = 80) {}
  /** Update the wrap width (terminal resized). Recompute how many physical rows the in-progress line now spans
   *  so the next feed()'s clear-math matches the new width (the terminal reflows the drawn text on resize too). */
  resize(cols: number): void {
    this.cols = Math.max(1, cols);
    if (this.buf) this.lineRows = Math.max(1, Math.ceil(this.buf.length / this.cols));
  }
  /** Render the held table block as aligned columns (shared with renderMdBlock) and reset the buffer. */
  private flushTable(): string {
    const rows = this.tableBuf;
    this.tableBuf = [];
    return renderTableLines(rows, this.p, this.cols).map((l) => l + '\r\n').join('');
  }
  feed(text: string): string {
    this.buf += text;
    let out = '';
    // 1. wipe the previously-drawn in-progress line (cursor is sitting at its end, possibly wrapped).
    if (this.lineRows > 1) out += `\x1b[${this.lineRows - 1}A\r\x1b[J`; // climb to its first row, clear down
    else if (this.lineRows === 1) out += '\r\x1b[K';
    this.lineRows = 0;
    // 2. commit every COMPLETE line (ending in \n). Table rows are HELD (block buffered, aligned at block end);
    //    any other line first flushes a pending table, then scrolls up and is never touched again.
    let nl: number;
    while ((nl = this.buf.indexOf('\n')) >= 0) {
      const line = this.buf.slice(0, nl);
      this.buf = this.buf.slice(nl + 1);
      if (!this.inFence && isTableRow(line)) { this.tableBuf.push(line); continue; }
      if (this.tableBuf.length) out += this.flushTable();
      const wasFence = this.inFence;
      const r = renderMdLine(line, this.inFence, this.p, this.cols, this.fenceLang);
      this.inFence = r.inFence;
      this.fenceLang = r.fenceLang;
      // word-wrap committed PROSE at the terminal width (code bodies + fence markers stay verbatim —
      // wrapping code would corrupt it; the terminal hard-wraps those rare overlong lines itself)
      out += (wasFence || r.inFence ? r.out : wrapAnsi(r.out, this.cols, hangIndent(line))) + '\r\n';
    }
    // 3. (re)draw the in-progress tail (no newline). A tail that STARTS with `|` while a table block is open is a
    //    partial table row still arriving — HOLD it (don't draw raw, it would glue onto the aligned table); it
    //    snaps in when its `\n` commits it or at flush(). A non-`|` tail means the table block ended, so emit the
    //    aligned table FIRST (correct order, no glue) before drawing the tail.
    const tailIsTableRow = this.tableBuf.length > 0 && !this.inFence && /^\s*\|/.test(this.buf);
    if (this.buf && !tailIsTableRow) {
      if (this.tableBuf.length) out += this.flushTable();
      const rendered = renderMdLine(this.buf, this.inFence, this.p, this.cols, this.fenceLang).out;
      if (this.inFence) { // code stays verbatim (terminal soft-wraps); rows from source length
        out += rendered;
        this.lineRows = Math.max(1, Math.ceil(this.buf.length / Math.max(1, this.cols)));
      } else {
        // word-wrap the tail too — a one-paragraph answer with no trailing \n NEVER commits, so
        // without this it would sit on screen terminal-hard-wrapped mid-word until the turn ends
        const tail = wrapAnsi(rendered, this.cols, hangIndent(this.buf));
        out += tail;
        this.lineRows = tail.split('\r\n').length; // rows = our explicit breaks (each row ≤ cols)
      }
    }
    return out;
  }
  /** Commit the in-progress line as-is (already on screen), emit any buffered table, and reset for next turn. */
  flush(): string {
    let out = '';
    // A table's final row usually arrives with no trailing newline — it's held in `buf` (feed step 3 does NOT
    // draw a partial table row, so nothing is on screen to clear). Fold a COMPLETE row into the block; an
    // incomplete partial (no closing `|`) is dropped rather than shown raw.
    if (this.tableBuf.length && this.buf && !this.inFence && isTableRow(this.buf)) {
      this.tableBuf.push(this.buf);
      this.buf = '';
    }
    out += this.tableBuf.length ? this.flushTable() : '';
    this.buf = '';
    this.inFence = false;
    this.fenceLang = undefined;
    this.lineRows = 0;
    return out;
  }
  pending(): boolean { return this.buf.length > 0 || this.tableBuf.length > 0; }
  /** Terminate the stream for external chrome (a tool header, a step notice): if an in-progress tail is
   *  drawn on screen, end its line with \r\n, then flush + reset. Without this, chrome printed below a
   *  pending tail leaves stale clear-math behind — the next feed() climbs the wrong rows and re-draws the
   *  old tail glued onto the new text (the classic duplicated/jammed-block artifact). */
  commit(): string {
    // a held partial table row (tailIsTableRow in feed) was never drawn → nothing on screen to terminate
    const open = this.buf.length > 0 && !(this.tableBuf.length > 0 && !this.inFence && /^\s*\|/.test(this.buf));
    return (open ? '\r\n' : '') + this.flush();
  }
}
